/**
 * Conversion between AdapterCdoLobbyVision2 (UI form state) and
 * Config.LobbyVision2TemplateT (FlatBuffer Object API) for binary save/load —
 * modeled on `utils/modbusFlatBuffer.ts`'s template pack/unpack.
 *
 * The LobbyVision2 CDO is now a bank/lift-agnostic TEMPLATE (see
 * `~/development/claude_plans/lobbyvision2_template_toggle_redesign.md`),
 * not the runtime `LobbyVision2.Mapping`. LiftCloud expands the template
 * per car/floor at CDO-generation time. The template entries carry the
 * authored telegraph op fields (telegraphType/operationId/operationType/
 * data) VERBATIM plus an LN2 message and the four expansion toggles
 * (unitsBased/floorsBased/bankLevel/individual) — there is no telegraph
 * bank_num/lift_num and no FrontEndMessage wrapping at this layer (those
 * only exist on the runtime `vendor/lobbyvision2.fbs` wire schema, which
 * the expander produces).
 *
 * Because the template still cannot go through the JSON `cdo_input` path
 * (LiftCloud decodes `cdo_binary` as `Config.LobbyVision2Template`,
 * mirroring the Modbus template binary path), it is packed to FlatBuffer
 * binary and sent via `cdo_binary` (create) or `PUT /adapters/:id/cdo`
 * (update); read back by decoding the adapter's `cdoBinary`.
 *
 * The LN2 side of each row (`Ln2MappingData.message`) is converted to/from
 * `Ln2.ValuedMessageT` via `compositeKeyToValuedMessageT`/
 * `valuedMessageTToCompositeKey` (`valuedMessageBuilder.ts`) — reused
 * rather than duplicated, per the original Architect review. `bank_id`/
 * `lift_id` on `Ln2MappingData` are NOT read by this builder (the editor
 * renders with `hideBankLift`) since the template never authors LN2
 * bank/lift identity.
 */
import * as flatbuffers from "flatbuffers";

import {
  LobbyVision2Template,
  LobbyVision2TemplateT,
} from "../generated/config/lobby-vision2-template";
import { LobbyVision2ReadEntryT } from "../generated/config/lobby-vision2-read-entry";
import { LobbyVision2WriteEntryT } from "../generated/config/lobby-vision2-write-entry";
import { LobbyVision2AddressingConfigT } from "../generated/config/lobby-vision2-addressing-config";
import { FrameType } from "../generated/lobby-vision2/frame-type";
import { OperationType } from "../generated/lobby-vision2/operation-type";
import { ValuedMessageT } from "../generated/ln2/valued-message";

import type {
  AdapterCdoLobbyVision2,
  LobbyVision2Addressing,
  LobbyVision2ReadRow,
  LobbyVision2WriteRow,
} from "../../interfaces/AdapterCdoLobbyVision2.interface";
import {
  BLANK_LOBBYVISION2_ADDRESSING,
  isValidSecureCarCalltype,
  liftNumSourceFromFb,
  liftNumSourceToFb,
  lobbyVision2RowNeedsCallType,
} from "../../interfaces/AdapterCdoLobbyVision2.interface";
import type { Ln2MappingData } from "../../pages/components/adapters/ln2MappingData";
import { defaultLn2MappingData } from "../../pages/components/adapters/ln2MappingData";
import {
  fbRefToCompositeKey,
  compositeKeyToFbRef,
} from "../../pages/components/adapters/ln2MappingConversions";
import {
  compositeKeyToValuedMessageT,
  valuedMessageTToCompositeKey,
} from "../types/valuedMessageBuilder";
import { packToBytes } from "../../utils/flatbufferApi";
import { opcUaMappingBytesToBase64 } from "./opcUaCdoBuilder";

// =====================================================================
// Ln2MappingData.message <-> ValuedMessageT
// =====================================================================

/**
 * Build a `ValuedMessageT` (content only) from `Ln2MappingData`. Returns
 * null when the row has no message selected — callers should skip
 * emitting an entry in that case (an entry with no LN2 message content is
 * meaningless: there is nothing to map the telegraph to/from).
 *
 * The `active` argument is hardcoded `true` and carries no meaning. A
 * dictionary row addresses a call; whether a given command energizes or
 * releases that call is runtime state, and the flag is stripped when VTS
 * builds its lookup map (the map is keyed on the inner `Message`, and
 * `on_off_state` is stamped from the live command's own `active`). It stays
 * `true` only so the bytes match every template already published — nothing
 * reads it, and `valuedMessageTToLn2MappingData` correctly drops it on the
 * way back.
 *
 * It was NOT always inert: while the map was keyed on the whole
 * `ValuedMessage`, this `true` meant every row matched only energize
 * commands, so releases missed the lookup outright and no call could be
 * cancelled.
 */
function ln2MappingDataToValuedMessageT(data: Ln2MappingData): ValuedMessageT | null {
  if (!data.message) return null;
  const key = fbRefToCompositeKey(data.message);
  if (!key) return null;
  return compositeKeyToValuedMessageT(key, true);
}

/**
 * Inverse of `ln2MappingDataToValuedMessageT`. `bank_id`/`bank_name`/
 * `lift_id`/`lift_name` are not carried on the template wire at all (the
 * template is bank/lift-agnostic), so they come back blank — the editor
 * renders with `hideBankLift` so this is expected, not a data loss bug.
 */
function valuedMessageTToLn2MappingData(vm: ValuedMessageT | null): Ln2MappingData {
  const blank = defaultLn2MappingData();
  const key = vm ? valuedMessageTToCompositeKey(vm) : null;
  const message = key ? compositeKeyToFbRef(key) : null;
  return { ...blank, message };
}

// =====================================================================
// LobbyVision2Addressing <-> LobbyVision2AddressingConfigT
// =====================================================================

function editorAddressingToFb(addressing: LobbyVision2Addressing): LobbyVision2AddressingConfigT {
  return new LobbyVision2AddressingConfigT(
    liftNumSourceToFb(addressing.liftNumSource),
    addressing.liftNumBase,
    addressing.bankLiftNum,
    addressing.floorDataBase,
    addressing.floorDataStep,
    addressing.applyVendorFloorOffset,
  );
}

/** Reconstruct the editor addressing config, defaulting to `BLANK_LOBBYVISION2_ADDRESSING` when the (optional) table is null so pre-config templates round-trip. */
function fbAddressingToEditor(fb: LobbyVision2AddressingConfigT | null): LobbyVision2Addressing {
  if (!fb) return { ...BLANK_LOBBYVISION2_ADDRESSING };
  return {
    liftNumSource: liftNumSourceFromFb(fb.liftNumSource),
    liftNumBase: fb.liftNumBase,
    bankLiftNum: fb.bankLiftNum,
    floorDataBase: fb.floorDataBase,
    floorDataStep: fb.floorDataStep,
    applyVendorFloorOffset: fb.applyVendorFloorOffset,
  };
}

// =====================================================================
// LobbyVision2ReadRow <-> LobbyVision2ReadEntryT
// =====================================================================

/** Convert a LobbyVision2ReadRow to a LobbyVision2ReadEntryT, or null when the row has no message selected. */
function editorReadRowToFb(row: LobbyVision2ReadRow): LobbyVision2ReadEntryT | null {
  const ln2Message = ln2MappingDataToValuedMessageT(row.message);
  if (!ln2Message) return null;
  const pairedLn2Message = row.pairedMessage
    ? ln2MappingDataToValuedMessageT(row.pairedMessage)
    : null;
  return new LobbyVision2ReadEntryT(
    row.telegraphType,
    row.operationId,
    row.operationType,
    row.data,
    ln2Message,
    row.floorsBased,
    row.unitsBased,
    row.bankLevel,
    row.individual,
    row.label,
    pairedLn2Message,
    liftNumSourceToFb(row.liftNumSourceOverride),
    row.liftNumBaseOverride,
    row.ccType,
    row.allocType,
  );
}

/**
 * Convert a LobbyVision2ReadEntryT back to a LobbyVision2ReadRow, or null when
 * the entry has no LN2 message content.
 *
 * Migration shim: pre-existing published templates authored the call type
 * straight into `data` (before `ccType` existed on the wire), so a template
 * saved before this change decodes with `ccType = 0` and the real call type
 * still sitting in `data`. Lift it into `ccType` (and clear `data`) the first
 * time such a row is read back, so the call type is not silently lost the
 * next time the template is opened and re-saved.
 */
function fbReadEntryToEditor(entry: LobbyVision2ReadEntryT): LobbyVision2ReadRow | null {
  if (!entry.ln2Message) return null;
  const label = typeof entry.label === "string" ? entry.label : null;
  const row: LobbyVision2ReadRow = {
    telegraphType: entry.telegraphType as FrameType,
    operationId: entry.operationId,
    operationType: entry.operationType as OperationType,
    data: entry.data,
    message: valuedMessageTToLn2MappingData(entry.ln2Message),
    floorsBased: entry.floorsBased,
    unitsBased: entry.unitsBased,
    bankLevel: entry.bankLevel,
    individual: entry.individual,
    liftNumSourceOverride: liftNumSourceFromFb(entry.liftNumSourceOverride),
    liftNumBaseOverride: entry.liftNumBaseOverride,
    label,
    pairedMessage: entry.pairedLn2Message
      ? valuedMessageTToLn2MappingData(entry.pairedLn2Message)
      : null,
    ccType: entry.ccType,
    allocType: entry.allocType,
  };
  if (lobbyVision2RowNeedsCallType(row) && !row.ccType && isValidSecureCarCalltype(row.data)) {
    return { ...row, ccType: row.data, data: 0 };
  }
  return row;
}

// =====================================================================
// LobbyVision2WriteRow <-> LobbyVision2WriteEntryT
// =====================================================================

/** Convert a LobbyVision2WriteRow to a LobbyVision2WriteEntryT, or null when the row has no message selected. */
function editorWriteRowToFb(row: LobbyVision2WriteRow): LobbyVision2WriteEntryT | null {
  const ln2Message = ln2MappingDataToValuedMessageT(row.message);
  if (!ln2Message) return null;
  return new LobbyVision2WriteEntryT(
    row.telegraphType,
    row.operationId,
    row.operationType,
    row.data,
    ln2Message,
    row.floorsBased,
    row.unitsBased,
    row.bankLevel,
    row.individual,
    row.label,
    liftNumSourceToFb(row.liftNumSourceOverride),
    row.liftNumBaseOverride,
    row.ccType,
    row.allocType,
  );
}

/**
 * Convert a LobbyVision2WriteEntryT back to a LobbyVision2WriteRow, or null
 * when the entry has no LN2 message content.
 *
 * Migration shim: see `fbReadEntryToEditor` — pre-existing published
 * templates authored the call type straight into `data`; lift it into
 * `ccType` (and clear `data`) the first time such a row is read back.
 */
function fbWriteEntryToEditor(entry: LobbyVision2WriteEntryT): LobbyVision2WriteRow | null {
  if (!entry.ln2Message) return null;
  const label = typeof entry.label === "string" ? entry.label : null;
  const row: LobbyVision2WriteRow = {
    telegraphType: entry.telegraphType as FrameType,
    operationId: entry.operationId,
    operationType: entry.operationType as OperationType,
    data: entry.data,
    message: valuedMessageTToLn2MappingData(entry.ln2Message),
    floorsBased: entry.floorsBased,
    unitsBased: entry.unitsBased,
    bankLevel: entry.bankLevel,
    individual: entry.individual,
    liftNumSourceOverride: liftNumSourceFromFb(entry.liftNumSourceOverride),
    liftNumBaseOverride: entry.liftNumBaseOverride,
    label,
    ccType: entry.ccType,
    allocType: entry.allocType,
  };
  if (lobbyVision2RowNeedsCallType(row) && !row.ccType && isValidSecureCarCalltype(row.data)) {
    return { ...row, ccType: row.data, data: 0 };
  }
  return row;
}

// =====================================================================
// Public API
// =====================================================================

/**
 * Build a `Config.LobbyVision2TemplateT` from the editor's
 * `AdapterCdoLobbyVision2` state. Rows that cannot be converted (no
 * message selected) are silently dropped — the editor's add/remove
 * controls are expected to keep rows valid, so this is a defensive filter
 * rather than the primary validation path (see `isValid` in
 * `LOBBYVISION2_BINARY_CDO_CONFIG`, `BinaryCdoProtocolRegistry.ts`).
 */
export function editorToLobbyVision2TemplateT(
  editor: AdapterCdoLobbyVision2,
): LobbyVision2TemplateT {
  const reads = editor.reads
    .map(editorReadRowToFb)
    .filter((e): e is LobbyVision2ReadEntryT => e !== null);
  const writes = editor.writes
    .map(editorWriteRowToFb)
    .filter((e): e is LobbyVision2WriteEntryT => e !== null);
  return new LobbyVision2TemplateT(reads, writes, editorAddressingToFb(editor.addressing));
}

/** Pack a `AdapterCdoLobbyVision2` editor state to FlatBuffer binary bytes. */
export function buildLobbyVision2TemplateBytes(editor: AdapterCdoLobbyVision2): Uint8Array {
  return packToBytes(editorToLobbyVision2TemplateT(editor));
}

/**
 * Base64-encode packed LobbyVision2 template bytes for the `cdo_binary`
 * request field. Reuses `opcUaMappingBytesToBase64` — the byte-encoding
 * step is protocol-agnostic, so this avoids a third copy of the same
 * byte->binary-string->btoa loop (a copy already exists in
 * `AdaptersTableActions.tsx`'s adapter export flow).
 */
export function lobbyVision2TemplateBytesToBase64(bytes: Uint8Array): string {
  return opcUaMappingBytesToBase64(bytes);
}

/** Decode packed LobbyVision2 template bytes back into `AdapterCdoLobbyVision2` editor state. */
export function decodeLobbyVision2Template(bytes: Uint8Array): AdapterCdoLobbyVision2 {
  const template = LobbyVision2Template.getRootAsLobbyVision2Template(
    new flatbuffers.ByteBuffer(bytes),
  ).unpack();
  const reads = template.reads
    .map(fbReadEntryToEditor)
    .filter((r): r is LobbyVision2ReadRow => r !== null);
  const writes = template.writes
    .map(fbWriteEntryToEditor)
    .filter((r): r is LobbyVision2WriteRow => r !== null);
  return { reads, writes, addressing: fbAddressingToEditor(template.addressing) };
}
